/**
 * THE WORLDS A BRAIN HOLDS — one roll-up, one label, one pick (Ruling 330).
 *
 * Ruling 329 made every folder of a brain a world on the front door. The set was
 * derived twice — once by the server for the wire, once by the reader for its counts
 * — and a test helper held a third hand-written copy. Three derivations of one set is
 * three chances to disagree about what a person's brain contains, and the second
 * review pass found them disagreeing in four separate ways: about a page nested two
 * folders deep, about a chain the reader folds and the wire did not, about a folder
 * that holds pages directly, and about a plane that cannot see an empty folder at all.
 *
 * SO THE DERIVATION IS HERE, in the contract, and there is exactly one of it. The
 * server rolls the wire's `worlds` off these functions; the browser rolls its own
 * counts and its skew fallback off the same ones; the compiled index names a page's
 * section with the same pick. A brain cannot be described two ways because there is
 * only one description.
 *
 * PURE AND BROWSER-SAFE. No node imports, no clock, no store — the reader ships this
 * to a phone, and the same call has to answer the same thing there as on Railway.
 *
 * EVERY CONTRACT READ IS AT CALL TIME (Ruling 219). Several server suites reach this
 * module's graph with a partial mock of `@bash/shared`, and an import-time read of a
 * key their factory omits fails collection.
 */

import { hiddenChainParts, MEMORY_ROOT, PAGE_SEED_FOLDERS, TREE } from './memory-tree.js';

/** One world on the front door: the folder, what it is called, and how much is in it. */
export interface NamiWorld {
  /** The folder's own last segment — the key every consumer derives its set from. */
  key: string;
  /** The folder's name as a person would write it (`humaniseFolder`). */
  label: string;
  /** How many PAGES it holds, chains folded. Never the folder rows beside them. */
  count: number;
}

/**
 * What the roll-up needs off a listing row, and nothing more.
 *
 * STRUCTURAL ON PURPOSE. The server's row and the browser's row are two declarations
 * of one wire shape, each with fields the other has no use for; naming only the five
 * this reads lets both hand their own rows over with no adapter between them.
 */
export interface NamiWorldEntry {
  path: string;
  /** What the page CALLS ITSELF. Absent on a folder and on a page that declares
   *  nothing — the chain fold asks it and takes the empty string for either. */
  title?: string | null;
  kind: 'file' | 'dir';
  tier: string;
  /** The world the server filed this row in, and null for a row filed in none. */
  pageKind?: string | null;
}

/** The knowledge tier as a page path spells it. Read at call time. */
const knowledgeDir = (): string => TREE.knowledge.root.slice(MEMORY_ROOT.length + 1);

/** The last segment of a slash-separated path. */
const lastSegment = (value: string): string => value.slice(value.lastIndexOf('/') + 1);

/**
 * A FOLDER'S NAME, AS A PERSON WOULD WRITE IT — the one label rule (Ruling 330).
 *
 * Sentence case rather than Title Case: a folder is a phrase a reader named, and
 * "Open Questions" is a headline. Three surfaces used to spell this — the wire's
 * label, the compiled index's section heading and the reader's own fallback — and a
 * folder could be called two things one click apart.
 *
 * NOT `humaniseStem`, which is the rule for a FILE name: that one capitalises every
 * word and keeps an ISO date prefix intact, because a page is named for a person or a
 * day. A folder is neither.
 */
export function humaniseFolder(slug: string): string {
  const words = slug.replace(/-+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The folders the contract files a world INSIDE — `entities/`, today.
 *
 * A seed with more than one segment is a DRAWER holding worlds rather than a world:
 * `entities/people` is where a person is filed, and `people` is the word the front
 * door's section is named after. Derived from the seed list, so a seed the contract
 * nests or unnests follows.
 */
export function drawerFolders(): ReadonlySet<string> {
  const nested = PAGE_SEED_FOLDERS.filter((folder) => folder.includes('/'));
  return new Set(nested.map((folder) => folder.slice(0, folder.indexOf('/'))));
}

/**
 * The worlds a brain is SEEDED with, in the order the front door reads them.
 *
 * SEEDS, NOT A LIST OF WORLDS. This decides ORDER, and — since Ruling 330 — that
 * these six are always ON the wire: a world the brain grew renders exactly as well,
 * after these, by weight.
 */
export function seededWorlds(): string[] {
  const learnings = TREE.knowledge.learnings.slice(TREE.knowledge.root.length + 1);
  return [...PAGE_SEED_FOLDERS, learnings].map(lastSegment);
}

/**
 * WHICH WORLD A ROW'S FOLDER CHAIN NAMES — the one pick a page and a folder row share.
 *
 * THE FIRST FOLDER BELOW THE TIER, because a world is a TOP-LEVEL thing on the front
 * door (Ruling 330). `projects/vestra/train-d.md` is filed in Projects; the sub-folder
 * is that world's own arrangement, not a world of its own. The enrichment already read
 * it this way and the compiled index read the LAST folder, so the agent's prompt and
 * the front door named two different worlds for one page.
 *
 * A DRAWER IS A SHELF UNTIL IT HOLDS A PAGE ITSELF. `entities/` is where people and
 * companies are filed, so `entities/people/zaf.md` is filed in People. A page sitting
 * DIRECTLY under the drawer is filed in the drawer — those words are somewhere, and a
 * world-less page is on no pill and in no count. But an empty drawer is not a world:
 * a folder ROW that is only the drawer names nothing, or every brain would carry an
 * "Entities" pill at zero that no page is ever under.
 *
 * The empty string for no world, never a world called `other`: a bucket for everything
 * the brain does not file would put a section on the front door holding the map, the
 * log and the kernel.
 */
export function worldOfFolders(folders: readonly string[], kind: 'file' | 'dir'): string {
  const head = folders[0];
  if (!head) return '';
  if (!drawerFolders().has(head)) return head;
  return folders[1] ?? (kind === 'file' ? head : '');
}

/**
 * THE WORLD A PAGE IS FILED IN, off its path alone.
 *
 * A leading tree root and a leading knowledge tier are both optional, so this reads a
 * page path spelled any of the ways this codebase holds one. The empty string for a
 * root file (the map, the log, how you work): they are written by deterministic
 * compilers rather than filed as pages, and none of them is in a world.
 */
export function pageWorldOf(path: string): string {
  const root = `${MEMORY_ROOT}/`;
  const belowRoot = path.startsWith(root) ? path.slice(root.length) : path;
  const tier = `${knowledgeDir()}/`;
  const rel = belowRoot.startsWith(tier) ? belowRoot.slice(tier.length) : belowRoot;
  const cut = rel.lastIndexOf('/');
  return cut < 0 ? '' : worldOfFolders(rel.slice(0, cut).split('/'), 'file');
}

/**
 * HOW MANY PAGES EACH WORLD HOLDS — the one fold, for the wire and for the reader
 * (Ruling 330).
 *
 * THE KNOWLEDGE TIER AND NOTHING ELSE. A `now/` row carries a world key too — the
 * scratchpad is one kind whatever depth it sits at — and the browser's count used to
 * take every row that carried one, so a brain that grew a `now/` FOLDER would have had
 * its week's pages added to that folder's count.
 *
 * A CHAIN IS ONE PAGE (Ruling 263). A page past the budget is continued into
 * `<stem>-<n>.md` beside it, and every list on the front door folds those back into
 * their head. The wire's count did not, so a person filed in three parts stood as
 * three people on the pill above a table that showed one.
 *
 * PAGES, NEVER FOLDERS. The listing carries a folder row too and gives it the same
 * world, so counting rows as they come would tell a reader with two people that they
 * have three. A folder row still MAKES the world, at zero: somebody made that drawer.
 */
export function worldCounts(entries: readonly NamiWorldEntry[]): Map<string, number> {
  const filed = entries.filter((e) => e.tier === 'knowledge');
  const hidden = hiddenChainParts(
    filed.filter((e) => e.kind === 'file').map((e) => ({ path: e.path, title: e.title ?? '' })),
  );
  const counts = new Map<string, number>();
  for (const entry of filed) {
    if (!entry.pageKind) continue;
    const held = counts.get(entry.pageKind) ?? 0;
    counts.set(entry.pageKind, held + (entry.kind === 'file' && !hidden.has(entry.path) ? 1 : 0));
  }
  return counts;
}

/**
 * THE WORLDS THIS LISTING HOLDS, in the order the front door draws them (Ruling 330).
 *
 * DERIVED FROM THE ROWS BEING SERVED, so the pills, the Browse cards, the tables and
 * the counts cannot disagree with each other or with the listing they ride on.
 *
 * THE SEEDED SIX ARE ALWAYS HERE, at zero when the rows do not show them. A published
 * archive holds only pages, so an empty seeded folder is invisible to every cold plane
 * — and a band that gained and lost pills as the answer came off one plane or another
 * would be telling a reader their brain changed shape when only the read did. Seeded
 * means present. A world the brain GREW appears only when it holds something, because
 * nothing else can know it exists.
 *
 * THE SEEDS KEEP THEIR PLACES so the door does not reshuffle under a reader as pages
 * land; a world the brain grew earns its place by weight, and ties break by key so one
 * brain always reads in one order.
 */
export function worldsOfEntries(entries: readonly NamiWorldEntry[]): NamiWorld[] {
  const counts = worldCounts(entries);
  const seeded = seededWorlds();
  for (const key of seeded) if (!counts.has(key)) counts.set(key, 0);
  const order = new Map(seeded.map((key, at) => [key, at]));
  const rank = (key: string): number => order.get(key) ?? Number.MAX_SAFE_INTEGER;
  return [...counts]
    .map(([key, count]) => ({ key, label: humaniseFolder(key), count }))
    .sort((a, b) => rank(a.key) - rank(b.key) || b.count - a.count || a.key.localeCompare(b.key));
}
